import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from "axios";
import queryString from "query-string";
import { getApiUrl } from "./getApiUrl";

export const RequestMethod = {
    GET: 'GET',
    POST: 'POST',
    DELETE: 'DELETE',
    PATCH: 'PATCH',
    PUT: 'PUT'
}

type RequestMethod = typeof RequestMethod[keyof typeof RequestMethod];

export type RequestProps = {
    method?: RequestMethod;
    url: string;
    query?: { [key: string]: string | number };
    headers?: any;
    params?: any;
    accessToken?: string;
    refreshToken?: string;
    fe?: boolean;
    skipAuth?: boolean;
};

// 토큰 갱신 중 여부
let isRefreshing = false;
// 토큰 갱신 대기 중인 요청들
let failedQueue: { resolve: (token: string) => void; reject: (error: any) => void }[] = [];

// 대기 중인 요청 처리
const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token!);
        }
    });
    failedQueue = [];
};

// 토큰 갱신 함수
const refreshToken = async (): Promise<string | null> => {
    const refreshTokenValue = localStorage.getItem("refreshToken");
    if (!refreshTokenValue) {
        return null;
    }

    try {
        const response = await axios.post(getApiUrl('ssoRefresh'), {
            refreshToken: refreshTokenValue
        });

        if (response.status === 200) {
            const newAccessToken = response.headers['accesstoken'] || response.data?.accessToken;
            const newRefreshToken = response.headers['refreshtoken'] || response.data?.refreshToken;

            if (newAccessToken) {
                localStorage.setItem("accessToken", newAccessToken);
            }
            if (newRefreshToken) {
                localStorage.setItem("refreshToken", newRefreshToken);
            }

            return newAccessToken;
        }
        return null;
    } catch (error) {
        console.error('Token refresh failed:', error);
        return null;
    }
};

// 로그인 페이지로 리다이렉트
const redirectToLogin = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");

    if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({
            type: 'TOKEN_EXPIRED',
            data: {}
        }));
    } else {
        const ssoUrl = process.env.REACT_APP_SSO_URL || 'http://localhost:3001';
        const returnUrl = encodeURIComponent(window.location.href);
        window.location.href = `${ssoUrl}/sign-in?serviceId=MOI&returnUrl=${returnUrl}`;
    }
};

// Instance 관련 설정
export const instance = axios.create({
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

// Request Interceptor - 요청에 토큰 추가
const requestConfig = (config: InternalAxiosRequestConfig) => {
    const accessToken = localStorage.getItem("accessToken");

    if (accessToken) {
        // 표준 Authorization Bearer 헤더
        config.headers['Authorization'] = `Bearer ${accessToken}`;
        // 하위 호환성을 위한 레거시 헤더
        config.headers['accesstoken'] = accessToken;
        config.headers['AccessToken'] = accessToken;
    }

    return config;
};

// Response Interceptor - 401 에러 시 토큰 갱신 후 재요청
const responseErrorHandler = async (error: AxiosError): Promise<AxiosResponse> => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // 401 에러이고, 재시도하지 않은 요청인 경우
    if (error.response?.status === 401 && !originalRequest._retry) {
        // refresh 요청 자체가 실패한 경우 로그인 페이지로 이동
        if (originalRequest.url?.includes('/auth/refresh')) {
            redirectToLogin();
            return Promise.reject(error);
        }

        // 토큰 갱신 중이면 대기열에 추가
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then(token => {
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                originalRequest.headers['accesstoken'] = token;
                originalRequest.headers['AccessToken'] = token;
                return instance(originalRequest);
            }).catch(err => {
                return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
            const newToken = await refreshToken();

            if (newToken) {
                processQueue(null, newToken);
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                originalRequest.headers['accesstoken'] = newToken;
                originalRequest.headers['AccessToken'] = newToken;
                return instance(originalRequest);
            } else {
                processQueue(error, null);
                redirectToLogin();
                return Promise.reject(error);
            }
        } catch (refreshError) {
            processQueue(refreshError, null);
            redirectToLogin();
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }

    return Promise.reject(error);
};

instance.interceptors.request.use(requestConfig);
instance.interceptors.response.use(
    (response) => response,
    responseErrorHandler
);

// 쿼리 파싱
const onParseQuery = (query?: { [key: string]: any } | string) => {
    if (!query) return '';
    if (typeof query === 'string') return `?${query}`;
    return '?' + queryString.stringify(query);
};

export const onRequest = async (props: RequestProps): Promise<AxiosResponse> => {
    let response: AxiosResponse;
    let axiosInstance: AxiosInstance = instance;

    try {
        response = await axiosInstance({
            method: props.method,
            url: props.url + onParseQuery(props.query),
            data: props.params,
            headers: props.headers,
        })
        return response;
    } catch (error: any) {
        return error.response || error;
    }
};
